import { Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { EMPTY, Observable, catchError, expand, finalize, forkJoin, map, of, shareReplay, toArray } from 'rxjs';
import { CommonConstants } from '@coreConstant';
import { PaginationModel, ProductModel, RequestParamModel } from '@coreModels';
import { SKIP_ERROR_TOAST } from '../interceptors/http-context.tokens';
import { ProductService } from './api-services/product.service';
import { DebugLoggerService } from './helpers/debug-logger.service';

/** What one card needs - never the SKU, which is not shown to the customer. */
export interface AiChatProductCardModel {
  name: string;
  url: string;
  image_url: string;
}

/**
 * Resolves the SKUs in a Pebbl `product_cards` frame to cards.
 *
 * The frame carries SKUs only - the AI service has no product endpoint - so name, imagery and
 * link come from the storefront's own product API, which keeps a card identical to the product
 * it links to.
 *
 * Resolved SKUs are cached for the life of the app: the store prefetches as soon as the frame
 * lands (before any reply text), the entries and the strip render from the same result, and
 * re-renders per token, reopening a conversation or the other chat surface never fetch again.
 */
@Injectable({
  providedIn: 'root'
})
export class AiChatProductCardsService {
  /** The server caps a reply at 50 cards, so one batch covers a whole strip. */
  private static readonly MAX_SKUS_PER_REQUEST = 50;
  /** Upper bound on pages followed for one batch, in case the pagination headers are wrong. */
  private static readonly MAX_PAGES = 5;
  private static readonly CARD_IMAGE_SIZE = '?tr=w-320,h-320,q-100,f-webp,e-sharpen-50';

  /** SKU -> card, or null when the product API did not return it (logged once, not retried). */
  private readonly resolved = new Map<string, AiChatProductCardModel | null>();
  /** SKU -> the request already fetching it, so a prefetch and a render share one call. */
  private readonly inFlight = new Map<string, Observable<void>>();

  constructor(
    private productService: ProductService,
    private debugLogger: DebugLoggerService
  ) { }

  /** Starts resolving early so imagery is ready by the time the entries render. */
  prefetch(skus: string[]): void {
    this.resolve(skus).subscribe();
  }

  /**
   * The cards for these SKUs, in the SKUs' order. A SKU that does not resolve is dropped and
   * logged - the backend only sends live products, so it is a lookup failure, never a reason to
   * fail the whole strip. A failed request yields the cards that did resolve (often none).
   */
  resolve(skus: string[]): Observable<AiChatProductCardModel[]> {
    return this.resolveEach(skus).pipe(
      map((cards) => cards.filter((card): card is AiChatProductCardModel => !!card))
    );
  }

  /**
   * As resolve(), but index for index with `skus`: null where a SKU did not resolve, so an entry
   * keeps its own picture when another SKU in the same reply fails. Every entry of a reply asks
   * with the reply's whole list, so they share one batch rather than a request each.
   */
  resolveEach(skus: string[]): Observable<(AiChatProductCardModel | null)[]> {
    const pending = [...new Set(skus.filter((sku) => !this.resolved.has(sku)))];
    this.chunk(pending.filter((sku) => !this.inFlight.has(sku))).forEach((batch) => this.fetch(batch));

    const waits = [...new Set(pending.map((sku) => this.inFlight.get(sku)).filter((item): item is Observable<void> => !!item))];
    if (!waits.length) {
      return of(this.collect(skus));
    }
    return forkJoin(waits).pipe(map(() => this.collect(skus)));
  }

  /**
   * One lookup for a batch of SKUs, registered so concurrent callers share it.
   *
   * Follows later pages until every SKU is found: the product API paginates on joined rows, not
   * products, so `pageSize=50` can return 42 products on page 1 and the other 8 on page 2.
   */
  private fetch(batch: string[]): void {
    const bySku = new Map<string, ProductModel>();
    let failed = false;

    const request = this.fetchPage(batch, 1).pipe(
      expand(({ pageNumber, page }) => {
        for (const product of page?.data ?? []) {
          if (typeof product?.sku === 'string') {
            bySku.set(product.sku, product);
          }
        }
        const isComplete = batch.every((sku) => bySku.has(sku));
        const next = pageNumber + 1;
        return !isComplete && next <= (page?.pageCount || 1) && next <= AiChatProductCardsService.MAX_PAGES
          ? this.fetchPage(batch, next)
          : EMPTY;
      }),
      catchError((error: unknown) => {
        // Missing SKUs are not cached: a network blip should not hide those cards for the rest
        // of the session. Products from pages that did arrive are still used.
        failed = true;
        this.debugLogger.warn('Pebbl product cards could not be loaded', error);
        return EMPTY;
      }),
      toArray(),
      map(() => {
        for (const sku of batch) {
          const product = bySku.get(sku);
          if (!product && failed) {
            continue;
          }
          const card = product ? this.toCard(product) : null;
          if (!card) {
            this.debugLogger.warn('Pebbl product card did not resolve', sku);
          }
          this.resolved.set(sku, card);
        }
      }),
      finalize(() => batch.forEach((sku) => this.inFlight.delete(sku))),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    batch.forEach((sku) => this.inFlight.set(sku, request));
  }

  private fetchPage(batch: string[], pageNumber: number): Observable<{ pageNumber: number; page: PaginationModel<ProductModel> }> {
    const requestParamModel = new RequestParamModel();
    requestParamModel.expand = ['formatHeroImage'];
    requestParamModel.fields = ['id', 'sku', 'family_name', 'finish_edge_name', 'title', 'url_slug'];
    // Percent-encodes each value - SKUs carry `/`, `+`, brackets and spaces.
    requestParamModel.filterInWithArray(batch, 'sku');
    requestParamModel.pageSize = batch.length;
    requestParamModel.pageIndex = pageNumber > 1 ? pageNumber : 0;

    // The strip handles its own failures (drop and log), so no global error toast over the chat.
    const context = new HttpContext().set(SKIP_ERROR_TOAST, true);
    return this.productService.getProduct(requestParamModel, context).pipe(
      map((page: PaginationModel<ProductModel>) => ({ pageNumber, page }))
    );
  }

  /** Cards in the frame's order, null where unresolved. Duplicates, if any, are kept. */
  private collect(skus: string[]): (AiChatProductCardModel | null)[] {
    return skus.map((sku) => this.resolved.get(sku) ?? null);
  }

  private toCard(product: ProductModel): AiChatProductCardModel | null {
    if (!product.url_slug) {
      return null;
    }
    const familyName = String(product.family_name ?? '').trim();
    const finish = String(product.finish_edge_name ?? '').trim();
    const name = familyName ? (finish ? `${familyName} - ${finish}` : familyName) : String(product.title ?? '').trim();
    if (!name) {
      return null;
    }
    const heroPath = product.formatHeroImage?.path;
    return {
      name,
      url: '/' + product.url_slug,
      image_url: heroPath
        ? `${CommonConstants.IMAGE_KIT_ENDPOINT}/${heroPath}${AiChatProductCardsService.CARD_IMAGE_SIZE}`
        : CommonConstants.NO_IMAGE_PRODUCT
    };
  }

  private chunk(skus: string[]): string[][] {
    const batches: string[][] = [];
    for (let index = 0; index < skus.length; index += AiChatProductCardsService.MAX_SKUS_PER_REQUEST) {
      batches.push(skus.slice(index, index + AiChatProductCardsService.MAX_SKUS_PER_REQUEST));
    }
    return batches;
  }
}
